import { useMemo } from "react";
import { Container, ProgressBar } from "./styles";
import { formatByteSize, truncarNomeArquivo } from "../../../../../utility/byteSizeUtils";
import { DotIcon, File, FileText, FileSpreadsheet, FileImage, FileArchive, FilePlay, FileType, FileVideoCamera, Loader2, CheckCircle2, AlertCircle, RotateCcw, X } from "lucide-react";
import { comum } from "../../../../../styles/Themes/light";

interface FileUploadInfo {
    file: File;
    progress: number;
    status: 'uploading' | 'success' | 'error';
    error?: string;
}

export default function CardAnexoUpload({ fileInfo, onRetry, onCancel }: { 
    fileInfo: FileUploadInfo, 
    onRetry?: () => void,
    onCancel?: () => void 
}) {
    const { file, progress, status, error } = fileInfo;

    const nome = useMemo(() => {
        return file.name;
    }, [file.name]);

    const nomeTruncado = useMemo(() => {
        return truncarNomeArquivo(nome);
    }, [nome]);

    const tamanho = useMemo(() => {
        return formatByteSize(file.size);
    }, [file.size]);

    const extensao = useMemo(() => {
        return nome.split('.').pop()?.toUpperCase() || '';
    }, [nome]);

    const icone = useMemo(() => {
        const ext = extensao.toLowerCase();
        
        if (ext === 'pdf') {
            return <FileText color={comum.colors.primaryRed400} />;
        }
        
        if (['doc', 'docx', 'rtf', 'odt', 'txt', 'pages'].includes(ext)) {
            return <FileType color={comum.colors.primaryBlue400} />;
        }
        
        if (['xls', 'xlsx', 'xlsm', 'xltx', 'ods', 'csv', 'tsv'].includes(ext)) {
            return <FileSpreadsheet color={comum.colors.secondaryGreen400} />;
        }
        
        if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'tiff', 'tif', 'svg', 'heic', 'heif'].includes(ext)) {
            return <FileImage color={comum.colors.secondaryPool400} />;
        }
        
        if (['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz', 'tgz', 'tbz'].includes(ext)) {
            return <FileArchive color={comum.colors.primaryOrange500} />;
        }
        
        if (['mp3', 'wav', 'aac', 'm4a', 'ogg', 'flac', 'wma'].includes(ext)) {
            return <FilePlay color={comum.colors.primaryBlue500} />;
        }
        
        if (['mp4', 'avi', 'mov', 'mkv', 'wmv', 'webm', 'm4v'].includes(ext)) {
            return <FileVideoCamera color={comum.colors.primaryOrange400} />;
        }
        
        return <File color={comum.colors.interfaceGray400} />;
    }, [extensao]);

    return (
        <Container $status={status}>
            <div className="icone">
                {icone}
            </div>
            <div className="meio">
                <div className="top">
                    <div className="nome" title={nome}>{nomeTruncado}</div>
                    <div className="extensao">{extensao}</div>
                </div>
                {status === 'uploading' && (
                    <ProgressBar $progress={progress}>
                        <div className="progress-bg">
                            <div className="progress-fill" />
                        </div>
                        <div className="progress-text">{progress}%</div>
                    </ProgressBar>
                )}
                <div className="bottom">
                    <div className="tamanho">{tamanho}</div>
                    <DotIcon size={16}/>
                    {status === 'uploading' && 'Enviando...'}
                    {status === 'success' && <span style={{ color: comum.colors.secondaryGreen400 }}>Enviado com sucesso</span>}
                    {status === 'error' && <span style={{ color: comum.colors.primaryRed400 }}>{error || 'Erro ao enviar'}</span>}
                </div>
            </div>
            <div className="action-icon">
                {status === 'uploading' && (
                    <Loader2 size={16} className="spinner" color={comum.colors.primaryRed400} />
                )}
                {status === 'success' && (
                    <CheckCircle2 size={16} color={comum.colors.secondaryGreen400} />
                )}
                {status === 'error' && (
                    <div className="error-buttons">
                        <button 
                            onClick={onRetry}
                            className="retry-button"
                            title="Tentar novamente"
                        >
                            <RotateCcw size={16} color={comum.colors.primaryRed400} />
                        </button>
                        <button 
                            onClick={onCancel}
                            className="cancel-button"
                            title="Cancelar"
                        >
                            <X size={16} color={comum.colors.interfaceGray400} />
                        </button>
                    </div>
                )}
            </div>
        </Container>
    );
}

